<script lang="ts">
/** One face in the stack. */
export interface GaiaAvatarStackItem {
  key: string;
  /** Two letters; the view derives them from the name. */
  initials: string;
  /** Tooltip / accessible name. */
  name?: string;
}
</script>

<script setup lang="ts">
import { computed, type PropType } from 'vue';
import GaiaAvatar from '../base/GaiaAvatar.vue';
import GaiaText from '../base/GaiaText.vue';
import { gaiaNeutral } from '../themes/tokens';

const props = defineProps({
  items: {
    type: Array as PropType<GaiaAvatarStackItem[]>,
    required: true,
  },
  /** Faces to draw before collapsing the rest into a "+N" disc. */
  max: {
    type: Number,
    default: 3,
  },
  size: {
    type: Number,
    default: 24,
  },
  /** Trailing label, e.g. "2 of 3 signed". */
  label: {
    type: String,
    default: undefined,
  },
});

const shown = computed(() => props.items.slice(0, props.max));
const overflow = computed(() => Math.max(0, props.items.length - props.max));

const surface = gaiaNeutral.surface;
const overlap = computed(() => `-${Math.round(props.size * 0.3)}px`);
</script>

<template>
  <!-- Overlapping signer faces from the requests list, with a "+N" disc and an
       optional count label. The overlap is a negative margin on every face but
       the first — no naive primitive stacks avatars. -->
  <n-flex align="center" :size="8" :wrap="false" class="gaia-avatar-stack">
    <n-flex inline :size="0" :wrap="false" :style="{ flex: 'none' }">
      <gaia-avatar
        v-for="item in shown"
        :key="item.key"
        class="face"
        :size="props.size"
        :title="item.name"
      >
        {{ item.initials }}
      </gaia-avatar>
      <!-- The overflow disc is a GaiaAvatar too, so its initials size off the
           same `size` prop as the faces it follows — a fixed font-size here
           drifted from them at every size but one. -->
      <gaia-avatar v-if="overflow" class="face" :size="props.size" :title="`${overflow} more`">
        +{{ overflow }}
      </gaia-avatar>
    </n-flex>
    <gaia-text
      v-if="props.label"
      size="control"
      tone="subtle"
      :style="{ fontVariantNumeric: 'tabular-nums' }"
    >
      {{ props.label }}
    </gaia-text>
  </n-flex>
</template>

<style scoped>
/* The ring is what separates one face from the one it overlaps. n-avatar sets
   `position: relative` on itself; keeping it explicit here means DOM order
   decides what covers what for every face, including the last. */
.face {
  position: relative;
  border: 1.5px solid v-bind(surface);
}

.face + .face {
  margin-left: v-bind(overlap);
}
</style>
